import { computed, onMounted, onUnmounted, ref, useId, type Ref } from 'vue'
import { generateGlassMaps } from '@/utils/liquidGlassMap'
import { isNativeApp } from '@/utils/platform'

export interface LiquidGlassLens {
  width: number
  height: number
  /** Escala para feDisplacementMap: desvío real = scale · (C/255 − ½). */
  scale: number
  mapUrl: string
}

interface CachedMaps {
  scale: number
  mapUrl: string
  specularUrl: string
}

const RESIZE_DEBOUNCE_MS = 120
const cache = new Map<string, CachedMaps>()

/** feDisplacementMap re-rasteriza por frame: caro en el WebView nativo o hardware limitado. */
function isRefractionDisabledByDevice(): boolean {
  if (isNativeApp()) return true
  const nav = navigator as Navigator & { deviceMemory?: number }
  if (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 4) return true
  if (typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency <= 4) return true
  return false
}

/** Solo Chromium aplica `backdrop-filter: url(#svg)`; WebKit y Gecko lo ignoran o lo rompen. */
export function supportsBackdropSvgFilter(): boolean {
  if (typeof navigator === 'undefined') return false
  const brands = (navigator as Navigator & { userAgentData?: { brands?: { brand: string }[] } }).userAgentData?.brands
  if (brands?.some(({ brand }) => brand === 'Chromium')) return true
  const ua = navigator.userAgent
  return /\bChrome\/\d+/.test(ua) && !/iPhone|iPad|iPod/.test(ua)
}

const toDataUrl = (pixels: Uint8ClampedArray, width: number, height: number): string => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) return ''
  const image = context.createImageData(width, height)
  image.data.set(pixels)
  context.putImageData(image, 0, 0)
  return canvas.toDataURL('image/png')
}

const whenIdle = (task: () => void): void => {
  if ('requestIdleCallback' in window) window.requestIdleCallback(task, { timeout: 300 })
  else setTimeout(task, 0)
}

/** Lente de vidrio líquido para un pill: genera mapas al medir y los regenera al cambiar tamaño o DPR. */
export function useLiquidGlass(target: Ref<HTMLElement | null>) {
  const filterId = `liquid-glass-${useId()}`
  const deviceAllowsRefraction = supportsBackdropSvgFilter() && !isRefractionDisabledByDevice()
  const canRefract = ref(false)
  const lens = ref<LiquidGlassLens | null>(null)
  const specularUrl = ref('')
  // Sin pausa al scrollear: alternar lente y blur se veía como un parpadeo roto. El costo lo acota el gating por dispositivo.
  const refraction = computed(() => canRefract.value)

  let observer: ResizeObserver | undefined
  let dprQuery: MediaQueryList | undefined
  let debounce: ReturnType<typeof setTimeout> | undefined
  let disposed = false

  function render(): void {
    const element = target.value
    if (disposed || !element) return
    const width = element.offsetWidth
    const height = element.offsetHeight
    if (width === 0 || height === 0) return
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 3)
    const key = `${width}x${height}@${pixelRatio}`
    let maps = cache.get(key)
    if (!maps) {
      const generated = generateGlassMaps({ width, height, radius: height / 2, pixelRatio })
      maps = {
        scale: generated.maxDisplacement * 2,
        mapUrl: toDataUrl(generated.displacement, generated.width, generated.height),
        specularUrl: toDataUrl(generated.specular, generated.width, generated.height)
      }
      cache.set(key, maps)
    }
    specularUrl.value = maps.specularUrl
    lens.value = maps.mapUrl ? { width, height, scale: maps.scale, mapUrl: maps.mapUrl } : null
    canRefract.value = deviceAllowsRefraction && lens.value !== null
  }

  function schedule(): void {
    clearTimeout(debounce)
    debounce = setTimeout(() => whenIdle(render), RESIZE_DEBOUNCE_MS)
  }

  // El media query de resolución solo avisa una vez: se re-registra con el DPR nuevo.
  function watchPixelRatio(): void {
    dprQuery?.removeEventListener('change', onPixelRatioChange)
    dprQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`)
    dprQuery.addEventListener('change', onPixelRatioChange)
  }

  function onPixelRatioChange(): void {
    watchPixelRatio()
    schedule()
  }

  onMounted(() => {
    if (!target.value) return
    observer = new ResizeObserver(schedule)
    observer.observe(target.value)
    watchPixelRatio()
    whenIdle(render)
  })

  onUnmounted(() => {
    disposed = true
    clearTimeout(debounce)
    observer?.disconnect()
    dprQuery?.removeEventListener('change', onPixelRatioChange)
  })

  return { filterId, refraction, lens, specularUrl }
}
